import React, { useState, useEffect } from 'react';
import { fmtNum, fmtFull } from '../utils/formatters';
import { Campaign } from '../data';
import { getCampaignCreativesSummary, CreativeSummary, getCampaigns, getReachCampaigns, getCreativeDetail, CreativeDetailResult, CreativeDetailScreen, getCreativeScreens, ScreenSummary, getCreativeLocationDemographics, getCreativeDemographics, getCreativeImpressionsByHour, getCreativeImpressionsByHourAverage, CreativeHourlyPoint, HourlyAveragePoint, groupScreensByLocation } from '../adapters/campaigns';
import { Demographics } from '../data';
import { NetworkMap } from './NetworkMap';
import { TimeSeriesChart } from './Chart';
import { DateRangePicker, last7DaysRange } from './DateRangePicker';
import { DemographicsCard } from './Demographics';
import { CreativeReport } from './CreativeReport';

// Same series keys/colors as the campaign page's "Delivery over time" chart (Dashboard.tsx SERIES).
const DAYPART_SERIES = [
  { key: 'plays',       label: 'Plays',       color: 'var(--series-plays)' },
  { key: 'impressions', label: 'Impressions', color: 'var(--series-impr)' },
];

function VideoThumb({ src, width = 40, height = 56, borderRadius = 6 }: { src: string; width?: number; height?: number; borderRadius?: number }) {
  return (
    <video
      src={src + '#t=0.001'}
      preload="metadata"
      muted
      playsInline
      style={{ width, height, borderRadius, objectFit: 'cover', display: 'block', flexShrink: 0 }}
    />
  );
}

function filenameFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    return decodeURIComponent(url.split('?')[0].split('/').pop() ?? '') || undefined;
  } catch {
    return url.split('?')[0].split('/').pop() || undefined;
  }
}

const SHOW_THEME: Record<string, { from: string; to: string; accent: string; logo: string; sub: string; glyph: string }> = {
  'Stranger Things': {
    from: '#7f1d1d', to: '#0c0a09',
    accent: '#dc2626',
    logo: 'STRANGER\nTHINGS',
    sub: 'THE FIRST SHADOW',
    glyph: '⚡',
  },
  'Harry Potter': {
    from: '#1e1b4b', to: '#020617',
    accent: '#a78bfa',
    logo: 'HARRY\nPOTTER',
    sub: 'AND THE CURSED CHILD',
    glyph: '✦',
  },
};

const STATUS_STYLE: Record<string, { bg: string; border: string; fg: string; dot: string }> = {
  Live:     { bg: 'var(--accent-soft-bg)',  border: 'var(--accent-soft-border)', fg: 'var(--brand-green-500)', dot: 'var(--brand-green-500)' },
  Pending:  { bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.30)',     fg: '#FF5C00',                dot: '#f59e0b' },
  Archived: { bg: 'var(--bg-surface-3)',    border: 'var(--border-soft)',        fg: 'var(--fg-muted)',        dot: 'var(--fg-faint)' },
};

function StatusPill({ status, size = 'md' }: { status: string; size?: string }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Archived;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: size === 'sm' ? 9.5 : 10.5, fontWeight: 700,
      background: s.bg, color: s.fg,
      border: '1px solid ' + s.border,
      padding: size === 'sm' ? '1px 7px' : '2px 9px', borderRadius: 999,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: '50%',
        background: s.dot,
        boxShadow: status === 'Live' ? `0 0 0 2.5px ${s.dot}33` : 'none',
      }} /> {status}
    </span>
  );
}

function CreativeThumb({ creative }: { creative: any }) {
  const theme = SHOW_THEME[creative.show] || { from: '#475569', to: '#0f172a', accent: '#fff', logo: 'CREATIVE', sub: '', glyph: '◯' };
  const isVideo = creative.type === 'Video';

  return (
    <div style={{
      position: 'relative',
      aspectRatio: '9 / 16',
      width: '100%',
      background: `linear-gradient(160deg, ${theme.from} 0%, ${theme.to} 100%)`,
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: 'inset 0 -30px 60px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)',
    }}>
      <svg width="100%" height="100%" viewBox="0 0 90 160" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, opacity: 0.22 }}>
        <defs>
          <radialGradient id={`grad-${creative.id}`} cx="50%" cy="35%" r="70%">
            <stop offset="0%" stopColor={theme.accent} stopOpacity="0.7" />
            <stop offset="100%" stopColor={theme.accent} stopOpacity="0" />
          </radialGradient>
          <pattern id={`dots-${creative.id}`} width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="4" cy="4" r="0.4" fill="#fff" opacity="0.5" />
          </pattern>
        </defs>
        <rect width="90" height="160" fill={`url(#dots-${creative.id})`} />
        <ellipse cx="45" cy="55" rx="55" ry="55" fill={`url(#grad-${creative.id})`} />
      </svg>

      <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 4 }}>
        <span style={{
          fontSize: 9, fontWeight: 800,
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          padding: '2px 7px', borderRadius: 999,
          display: 'inline-flex', alignItems: 'center', gap: 3,
          backdropFilter: 'blur(4px)',
        }}>
          {isVideo ? '▶' : '◐'} {creative.type.toUpperCase()}
        </span>
      </div>

      <div style={{
        position: 'absolute', top: 8, right: 8,
        fontSize: 9.5, fontWeight: 800,
        background: 'rgba(0,0,0,0.55)', color: '#fff',
        padding: '2px 7px', borderRadius: 4,
        backdropFilter: 'blur(4px)',
        fontVariantNumeric: 'tabular-nums',
      }}>{creative.dur}</div>

      <div style={{
        position: 'absolute', top: '38%', left: 0, right: 0,
        transform: 'translateY(-50%)',
        textAlign: 'center',
        color: '#fff',
        textShadow: '0 2px 12px rgba(0,0,0,0.6)',
      }}>
        <div style={{ fontSize: 28, color: theme.accent, opacity: 0.5, marginBottom: 4, lineHeight: 1 }}>{theme.glyph}</div>
        <div style={{
          fontSize: 14, fontWeight: 900,
          letterSpacing: '0.06em',
          lineHeight: 1.05,
          whiteSpace: 'pre-line',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>{theme.logo}</div>
        <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '0.18em', opacity: 0.75, marginTop: 4 }}>{theme.sub}</div>
      </div>

      <div style={{
        position: 'absolute', bottom: 10, left: 0, right: 0,
        textAlign: 'center', color: '#fff',
        fontSize: 8.5, fontWeight: 700,
        letterSpacing: '0.16em', opacity: 0.85,
      }}>
        MARQUIS THEATRE · NYC
      </div>

      {isVideo && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0)',
          opacity: 0,
          transition: 'opacity 0.15s, background 0.15s',
          pointerEvents: 'none',
        }} className="creative-play-overlay">
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,255,255,0.95)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#0f172a', fontSize: 16, paddingLeft: 3,
          }}>▶</div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: 8.5, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        color: 'var(--fg-muted)', marginBottom: 2, whiteSpace: 'nowrap',
      }}>{label}</div>
      <div className="num" style={{
        fontSize: 12.5, fontWeight: 700,
        color: highlight ? 'var(--brand-green-500)' : 'var(--fg-strong)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{value}</div>
    </div>
  );
}

function relTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date(2026, 4, 24);
  const days = Math.round((now.getTime() - d.getTime()) / 864e5);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return days + 'd ago';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CreativeCard({ creative, onOpen }: { creative: any; onOpen: (c: any) => void }) {
  return (
    <div className="card" style={{
      padding: 12, display: 'flex', flexDirection: 'column', gap: 12,
      cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s',
    }}
      onClick={() => onOpen(creative)}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card-hi)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <CreativeThumb creative={creative} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 700,
          background: creative.show === 'Stranger Things' ? 'rgba(220,38,38,0.10)' : 'rgba(167,139,250,0.14)',
          color:      creative.show === 'Stranger Things' ? '#dc2626' : '#7c3aed',
          padding: '2px 8px', borderRadius: 999, letterSpacing: '0.04em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%',
        }}>{creative.show}</span>
        <StatusPill status={creative.status} size="sm" />
      </div>

      <div style={{
        fontSize: 12.5, fontWeight: 700, color: 'var(--fg-strong)', lineHeight: 1.3,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', minHeight: 32,
      }}>{creative.name}</div>

      {creative.status !== 'Pending' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, padding: '10px 0 0', borderTop: '1px solid var(--border-subtle)' }}>
          <Metric label="Plays" value={fmtNum(creative.plays)} />
          <Metric label="Views" value={fmtNum(creative.views)} highlight />
          <Metric label="Compl." value={creative.completion != null ? creative.completion + '%' : '—'} />
        </div>
      ) : (
        <div style={{
          padding: '10px 8px', textAlign: 'center',
          borderTop: '1px solid var(--border-subtle)',
          color: 'var(--fg-muted)', fontSize: 11, fontStyle: 'italic',
        }}>
          Awaiting review · uploaded <span className="num">{relTime(creative.uploaded)}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, color: 'var(--fg-faint)', marginTop: -4 }}>
        <span className="num">{creative.id}</span>
        <span className="num">{creative.size}</span>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)' }}>{value}</div>
    </div>
  );
}

function BigStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 3 }}>{label}</div>
      <div className="num" style={{ fontSize: 18, fontWeight: 700, color: highlight ? 'var(--brand-green-500)' : 'var(--fg-strong)' }}>{value}</div>
    </div>
  );
}

function CreativeDetail({ creative, onClose }: { creative: any; onClose: () => void }) {
  if (!creative) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 50,
      }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(480px, 92vw)',
        background: 'var(--bg-surface)',
        boxShadow: '-20px 0 50px rgba(0,0,0,0.20)',
        zIndex: 51, display: 'flex', flexDirection: 'column',
        borderLeft: '1px solid var(--border-subtle)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>Creative</div>
            <div className="num" style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-strong)' }}>{creative.id}</div>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8,
            border: '1px solid var(--border-soft)', background: 'transparent',
            color: 'var(--fg-default)', fontSize: 14, cursor: 'pointer',
          }}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ width: 200, alignSelf: 'center' }}>
            <CreativeThumb creative={creative} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 10.5, fontWeight: 700,
                background: creative.show === 'Stranger Things' ? 'rgba(220,38,38,0.10)' : 'rgba(167,139,250,0.14)',
                color:      creative.show === 'Stranger Things' ? '#dc2626' : '#7c3aed',
                padding: '2px 9px', borderRadius: 999,
              }}>{creative.show}</span>
              <StatusPill status={creative.status} />
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em', marginBottom: 4 }}>{creative.name}</h2>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{creative.advertiser}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <DetailRow label="Type" value={creative.type} />
            <DetailRow label="Duration" value={creative.dur} />
            <DetailRow label="Format" value={creative.size} />
            <DetailRow label="Uploaded" value={new Date(creative.uploaded).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
          </div>

          {creative.status !== 'Pending' && (
            <div style={{ padding: '14px 16px', background: 'var(--bg-surface-2)', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 10 }}>Lifetime delivery</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                <BigStat label="Plays" value={fmtFull(creative.plays)} />
                <BigStat label="Impressions" value={fmtFull(creative.impressions)} />
                <BigStat label="Measurable" value={fmtFull(creative.measurable)} />
                <BigStat label="Verified Views" value={fmtFull(creative.views)} highlight />
              </div>
              {creative.completion != null && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 11 }}>
                    <span style={{ color: 'var(--fg-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Completion rate</span>
                    <span className="num" style={{ fontWeight: 700, color: 'var(--brand-green-500)' }}>{creative.completion}%</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--bg-surface-3)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${creative.completion}%`, height: '100%', background: 'var(--brand-green-500)', borderRadius: 999 }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {creative.status === 'Pending' && (
            <div style={{
              padding: '14px 16px',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.25)',
              borderRadius: 10,
              fontSize: 12, color: '#FF5C00', lineHeight: 1.5,
            }}>
              <strong>Awaiting Smartstreet review.</strong><br />
              Creatives are typically approved within 4 business hours during ad ops coverage (9a–7p ET).
            </div>
          )}
        </div>

        <div style={{ padding: 16, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8 }}>
          <button style={{
            flex: 1, padding: '10px 14px',
            background: 'transparent', color: 'var(--fg-default)',
            border: '1px solid var(--border-soft)', borderRadius: 10,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Download asset</button>
          <button style={{
            flex: 1, padding: '10px 14px',
            background: 'var(--brand-navy-800)', color: '#fff',
            border: 'none', borderRadius: 10,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Open in POP report →</button>
        </div>
      </div>
    </>
  );
}

function SummaryStrip({ creatives }: { creatives: any[] }) {
  const live = creatives.filter(c => c.status === 'Live');
  const pending = creatives.filter(c => c.status === 'Pending');
  const videos = creatives.filter(c => c.type === 'Video');
  const topPerformer = [...live].sort((a, b) => b.views - a.views)[0];

  const items = [
    { label: 'Total Creatives', value: creatives.length, sub: videos.length + ' video · ' + (creatives.length - videos.length) + ' static' },
    { label: 'Live', value: live.length, sub: 'Delivering now', dot: 'var(--brand-green-500)' },
    { label: 'Pending Review', value: pending.length, sub: pending.length > 0 ? 'Needs approval' : 'All clear', dot: pending.length > 0 ? '#f59e0b' : undefined },
    {
      label: 'Top Performer',
      value: topPerformer ? fmtNum(topPerformer.views) : '—',
      sub: topPerformer ? 'views · ' + topPerformer.name.slice(0, 28) + (topPerformer.name.length > 28 ? '…' : '') : '',
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
      {items.map(it => (
        <div key={it.label} className="card" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            {it.dot && <span style={{
              width: 6, height: 6, borderRadius: '50%', background: it.dot,
              boxShadow: it.label === 'Live' ? `0 0 0 3px ${it.dot}33` : 'none',
            }} />}
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>{it.label}</span>
          </div>
          <div className="num" style={{ fontSize: 26, fontWeight: 700, color: 'var(--fg-strong)', lineHeight: 1, marginBottom: 4 }}>{it.value}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</div>
        </div>
      ))}
    </div>
  );
}


function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 10 }}>{children}</div>
  );
}

function CreativeDetailPage({ creative, campaignUuids, onBack }: {
  creative: CreativeSummary;
  campaignUuids: string;
  onBack: () => void;
}) {
  // Local, user-adjustable date range for this creative's report. Defaults to
  // the last 7 days, matching the picker's own "Last 7 days" preset. Lazy
  // initializers so last7DaysRange() only runs once, on mount.
  const [startDate, setStartDate] = useState(() => last7DaysRange().start);
  const [endDate, setEndDate] = useState(() => last7DaysRange().end);
  const [detail, setDetail] = useState<CreativeDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Screens this creative ran on, straight from getCreativeScreens (lat/lon
  // included) — the endpoint is itself the live registry query, so there's
  // no separate "does this screen still exist" filter needed.
  const [creativeScreens, setCreativeScreens] = useState<ScreenSummary[]>([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Demographics per distinct sensor location, keyed by SensorLocation.key.
  // Absent key = not fetched/still loading; locationDemographicsLoading tracks that.
  const [locationDemographics, setLocationDemographics] = useState<Record<string, Demographics>>({});
  const [locationDemographicsLoading, setLocationDemographicsLoading] = useState<Record<string, boolean>>({});
  // Aggregate demographics across every screen this creative played on
  // (backend-summed, not a client-side average of the per-screen shares above).
  const [totalDemographics, setTotalDemographics] = useState<Demographics | null>(null);
  const [totalDemographicsLoading, setTotalDemographicsLoading] = useState(false);
  // Delivery (plays/impressions) bucketed by hour-of-day, for the "by daypart" chart.
  const [impressionsByHour, setImpressionsByHour] = useState<CreativeHourlyPoint[]>([]);
  const [impressionsByHourLoading, setImpressionsByHourLoading] = useState(false);
  // True per-day average delivery by hour-of-day, for the export report's
  // "When the audience showed up" chart (real volume, not a play ratio).
  const [impressionsByHourAverage, setImpressionsByHourAverage] = useState<HourlyAveragePoint[]>([]);
  // Which single metric the daypart chart plots — mirrors the campaign
  // page's toggle pills, which are radio-style (one metric at a time).
  const [activeDaypartMetric, setActiveDaypartMetric] = useState<string[]>(['plays']);

  const isVideo = creative.type === 'video' || /\.(mp4|mov|webm)$/i.test(creative.creativeUrl ?? '');
  const filename = filenameFromUrl(creative.creativeUrl);
  const displayName = creative.name ?? filename ?? 'Creative';

  const visibleScreens: CreativeDetailScreen[] = creativeScreens.map(s => ({
    id: s.id, name: s.name, address: s.venue, plays: s.plays, impressions: s.impressions, sensorEnabled: s.sensorEnabled,
  }));
  const mapScreens = creativeScreens.map(s => ({ id: s.id, name: s.name, venue: s.venue, lat: s.lat, lon: s.lon }));
  const sensorScreens = visibleScreens.filter(s => s.sensorEnabled);
  const sensorLocations = groupScreensByLocation(sensorScreens);
  const daypartTotals = {
    plays: impressionsByHour.reduce((sum, d) => sum + d.plays, 0),
    impressions: impressionsByHour.reduce((sum, d) => sum + d.impressions, 0),
  };
  // For the "By location" breakdown: keep locations still loading (so their
  // skeleton shows) or that resolved with real data; drop ones that came
  // back with nothing for this range instead of showing an empty card.
  const sensorLocationsWithData = sensorLocations.filter(l => locationDemographicsLoading[l.key] || locationDemographics[l.key]);

  useEffect(() => {
    const creativeUrl = creative.creativeUrl;
    if (!creativeUrl || !campaignUuids) return;
    setTotalDemographicsLoading(true);
    setTotalDemographics(null);
    getCreativeDemographics(creativeUrl, campaignUuids, startDate, endDate || undefined)
      .then(setTotalDemographics)
      .catch(() => setTotalDemographics(null))
      .finally(() => setTotalDemographicsLoading(false));
  }, [creative.creativeUrl, campaignUuids, startDate, endDate]);

  useEffect(() => {
    const creativeUrl = creative.creativeUrl;
    if (!creativeUrl || !campaignUuids) return;
    setImpressionsByHourLoading(true);
    setImpressionsByHour([]);
    getCreativeImpressionsByHour(creativeUrl, campaignUuids, startDate, endDate || undefined)
      .then(setImpressionsByHour)
      .catch(() => setImpressionsByHour([]))
      .finally(() => setImpressionsByHourLoading(false));
  }, [creative.creativeUrl, campaignUuids, startDate, endDate]);

  useEffect(() => {
    const creativeUrl = creative.creativeUrl;
    if (!creativeUrl) return;
    setImpressionsByHourAverage([]);
    // No campaign_uuids — same reasoning as getCreativeDetail/getCreativeScreens:
    // this creative can span campaigns outside the current client's list, and
    // the endpoint's creative_url filter is sufficient on its own.
    getCreativeImpressionsByHourAverage(creativeUrl, undefined, startDate, endDate || undefined)
      .then(setImpressionsByHourAverage)
      .catch(() => setImpressionsByHourAverage([]));
  }, [creative.creativeUrl, startDate, endDate]);

  useEffect(() => {
    const creativeUrl = creative.creativeUrl;
    if (!creativeUrl) return;
    setDetailLoading(true);
    // No campaign_uuids — this creative can span campaigns outside the
    // current client's list, and the endpoint's creative_url filter is
    // sufficient on its own.
    getCreativeDetail(creativeUrl, undefined, startDate, endDate || undefined)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [creative.creativeUrl, startDate, endDate]);

  useEffect(() => {
    const creativeUrl = creative.creativeUrl;
    if (!creativeUrl) return;
    setScreensLoading(true);
    setCreativeScreens([]);
    setLocationDemographics({});
    setLocationDemographicsLoading({});
    // No campaign_uuids here either — same reasoning as getCreativeDetail
    // above, and getCreativeScreens is itself the live screens-registry
    // query, so there's no separate "does this screen still exist" filter
    // needed the way the old getScreensByBroadsignIds round trip required.
    getCreativeScreens(creativeUrl, undefined, startDate, endDate || undefined)
      .then(results => {
        setCreativeScreens(results);

        // Group sensor-enabled screens by physical location and fetch one
        // demographic breakdown per location (backend-summed across its
        // screens), not one per screen.
        const withSensors = results.filter(s => s.sensorEnabled)
          .map(s => ({ id: s.id, name: s.name, address: s.venue, plays: s.plays, impressions: s.impressions, sensorEnabled: s.sensorEnabled }));
        const locations = groupScreensByLocation(withSensors);
        setLocationDemographicsLoading(Object.fromEntries(locations.map(l => [l.key, true])));
        locations.forEach(l => {
          getCreativeLocationDemographics(l.screenIds, creativeUrl, campaignUuids, startDate, endDate || undefined)
            .then(data => setLocationDemographics(prev => ({ ...prev, [l.key]: data })))
            .catch(() => {})
            .finally(() => setLocationDemographicsLoading(prev => ({ ...prev, [l.key]: false })));
        });
      })
      .catch(() => setCreativeScreens([]))
      .finally(() => setScreensLoading(false));
  }, [creative.creativeUrl, campaignUuids, startDate, endDate]);

  return (
    <div>
      {exportOpen && (
        <CreativeReport
          creative={creative}
          startDate={startDate} endDate={endDate}
          campaigns={detail?.campaigns ?? []}
          screens={visibleScreens}
          mapScreens={mapScreens}
          hourlyAverage={impressionsByHourAverage}
          totalDemographics={totalDemographics}
          onClose={() => setExportOpen(false)}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid var(--border-soft)', background: 'transparent',
              color: 'var(--fg-muted)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ← Back
          </button>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          <span style={{
            flexShrink: 0, fontSize: 10.5, fontWeight: 700,
            color: isVideo ? 'var(--brand-blue)' : '#FF5C00',
            background: isVideo ? 'rgba(59,130,246,0.10)' : 'rgba(245,158,11,0.12)',
            border: `1px solid ${isVideo ? 'rgba(59,130,246,0.20)' : 'rgba(245,158,11,0.25)'}`,
            padding: '2px 9px', borderRadius: 5,
          }}>
            {isVideo ? '▶ Video' : '◻ Image'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={(s, e) => { setStartDate(s); setEndDate(e); }} align="right" />
          <button
            onClick={() => setExportOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--brand-navy-800)', color: '#fff', border: 'none',
              padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Export
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
              {[
                { label: 'Total Plays', value: creative.plays ? fmtFull(creative.plays) : '—', highlight: false },
                { label: 'Total Impressions', value: creative.impressions ? fmtFull(creative.impressions) : '—', highlight: true },
              ].map(stat => (
                <div key={stat.label} className="card" style={{ padding: '20px 22px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 8 }}>{stat.label}</div>
                  <div className="num" style={{ fontSize: 28, fontWeight: 700, color: stat.highlight ? 'var(--brand-green-500)' : 'var(--fg-strong)', lineHeight: 1 }}>{stat.value}</div>
                </div>
              ))}
            </div>

            {/* Details + preview */}
            <div className="card" style={{ padding: '18px 20px' }}>
              <SectionLabel>Details</SectionLabel>
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div
                  onClick={() => creative.creativeUrl && setMediaOpen(true)}
                  style={{ flexShrink: 0, width: 120, borderRadius: 8, overflow: 'hidden', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', cursor: creative.creativeUrl ? 'zoom-in' : 'default', position: 'relative' }}
                >
                  {creative.creativeUrl ? (
                    isVideo ? (
                      <video src={creative.creativeUrl + '#t=0.001'} preload="metadata" muted playsInline style={{ width: '100%', display: 'block' }} />
                    ) : (
                      <img src={creative.creativeUrl} alt={displayName} style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                    )
                  ) : (
                    <div style={{ aspectRatio: '9/16', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)', fontSize: 22 }}>
                      {isVideo ? '▶' : '◻'}
                    </div>
                  )}
                  {creative.creativeUrl && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0)', opacity: 0, transition: 'opacity 0.15s, background 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0'; e.currentTarget.style.background = 'rgba(0,0,0,0)'; }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#0f172a' }}>
                        {isVideo ? '▶' : '⤢'}
                      </div>
                    </div>
                  )}
                </div>

                {mediaOpen && creative.creativeUrl && (
                  <div
                    onClick={() => setMediaOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
                  >
                    <button onClick={() => setMediaOpen(false)} style={{ position: 'absolute', top: 20, right: 20, width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    <div onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: 12, overflow: 'hidden' }}>
                      {isVideo
                        ? <video src={creative.creativeUrl} controls autoPlay playsInline style={{ maxHeight: '90vh', maxWidth: '90vw', display: 'block', borderRadius: 12 }} />
                        : <img src={creative.creativeUrl} alt={displayName} style={{ maxHeight: '90vh', maxWidth: '90vw', display: 'block', borderRadius: 12, objectFit: 'contain' }} />
                      }
                    </div>
                  </div>
                )}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { label: 'Type', value: isVideo ? 'Video' : 'Image' },
                    ...(filename ? [{ label: 'Filename', value: filename }] : []),
                    ...(creative.creativeUrl ? [{ label: 'URL', value: creative.creativeUrl }] : []),
                  ].map(row => (
                    <div key={row.label}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 3 }}>{row.label}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-strong)', wordBreak: 'break-all', lineHeight: 1.4 }}>{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Delivery by daypart — mirrors the "Delivery over time" chart on the campaign page */}
            <div className="card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Delivery by daypart</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {DAYPART_SERIES.map(s => {
                    const on = activeDaypartMetric.includes(s.key);
                    return (
                      <button key={s.key} onClick={() => setActiveDaypartMetric([s.key])} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '7px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', border: '1.5px solid ' + (on ? s.color : 'var(--border-soft)'),
                        background: on ? s.color + '18' : 'transparent',
                        color: on ? s.color : 'var(--fg-muted)',
                        transition: 'all 0.15s',
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: on ? s.color : 'var(--fg-faint)', flexShrink: 0 }} />
                        <span>{s.label}</span>
                        <span className="num" style={{ fontSize: 13, fontWeight: 800, color: on ? s.color : 'var(--fg-muted)', marginLeft: 2 }}>
                          {fmtFull(daypartTotals[s.key as keyof typeof daypartTotals])}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {impressionsByHourLoading ? (
                <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      border: '3px solid var(--border-soft)',
                      borderTopColor: 'var(--series-impr)',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Loading delivery data…</span>
                  </div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : impressionsByHour.length > 0 ? (
                <TimeSeriesChart
                  data={impressionsByHour.map(d => ({ label: d.hour, plays: d.plays, impressions: d.impressions }))}
                  seriesConfig={DAYPART_SERIES}
                  activeKeys={activeDaypartMetric}
                  height={220}
                  showYear={false}
                />
              ) : (
                <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
                  No delivery data for this range
                </div>
              )}
            </div>

            {/* Campaigns */}
            <div className="card" style={{ padding: '18px 20px' }}>
              <SectionLabel>Campaigns</SectionLabel>
              {detailLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[1, 2].map(i => <div key={i} className="skeleton" style={{ height: 36, borderRadius: 6 }} />)}
                </div>
              ) : !detail?.campaigns.length ? (
                <div style={{ fontSize: 12, color: 'var(--fg-faint)', padding: '8px 0' }}>No campaigns found</div>
              ) : (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {detail.campaigns.map((c, i) => (
                    <div key={c.uuid}
                      onClick={() => {
                        window.history.pushState(null, '', `/campaigns/${c.uuid}`);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 90px 110px',
                        alignItems: 'center', gap: 12,
                        borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                        cursor: 'pointer', transition: 'background 0.1s',
                        margin: '0 -20px', padding: '10px 20px',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <div className="num" style={{ textAlign: 'right', fontSize: 12, color: 'var(--fg-default)' }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Plays</div>
                        {fmtFull(c.plays)}
                      </div>
                      <div className="num" style={{ textAlign: 'right', fontSize: 12, color: 'var(--brand-green-500)', fontWeight: 700 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Impressions</div>
                        {fmtFull(c.impressions)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Screens + Map side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: mapScreens.length > 0 ? '1fr 1fr' : '1fr', gap: 16, alignItems: 'start' }}>
              <div className="card" style={{ padding: '18px 20px' }}>
                <SectionLabel>Screens</SectionLabel>
                {screensLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 36, borderRadius: 6 }} />)}
                  </div>
                ) : !visibleScreens.length ? (
                  <div style={{ fontSize: 12, color: 'var(--fg-faint)', padding: '8px 0' }}>No screens found</div>
                ) : (
                  <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                    {visibleScreens.map((s, i) => (
                      <div key={s.id}
                        onClick={() => {
                          window.history.pushState(null, '', `/screens?screen_id=${encodeURIComponent(s.id)}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr 70px 90px',
                          alignItems: 'center',
                          borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                          gap: 8, cursor: 'pointer', transition: 'background 0.1s',
                          margin: '0 -20px', padding: '10px 20px',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                          {s.address && <div style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.address}</div>}
                        </div>
                        <div className="num" style={{ textAlign: 'right', fontSize: 12, color: 'var(--fg-default)' }}>
                          <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Plays</div>
                          {fmtFull(s.plays)}
                        </div>
                        <div className="num" style={{ textAlign: 'right', fontSize: 12, color: 'var(--brand-green-500)', fontWeight: 700 }}>
                          <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Impressions</div>
                          {fmtFull(s.impressions)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {mapScreens.length > 0 && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-strong)' }}>Locations</div>
                    <span style={{ fontSize: 11, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-soft)', padding: '2px 9px', borderRadius: 999 }}>{mapScreens.length}</span>
                  </div>
                  <div style={{ padding: 18 }}>
                    <NetworkMap screens={mapScreens} maxWidth={560} />
                  </div>
                </div>
              )}
            </div>

            {/* Audience: sensor-verified demographics for this creative — an aggregate
                total across every screen, plus a breakdown per distinct sensor
                location (screens sharing an address are grouped into one card,
                since co-located screens often share a single physical sensor
                and would otherwise show identical, duplicated numbers). */}
            {(sensorLocations.length > 0 || totalDemographicsLoading || totalDemographics) && (
              <div className="card" style={{ padding: '18px 20px' }}>
                <SectionLabel>Audience</SectionLabel>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 14, lineHeight: 1.5 }}>
                  Anonymous, sensor-verified demographics captured while this creative played.
                </div>

                <div style={{ marginBottom: sensorLocationsWithData.length > 0 ? 20 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 8 }}>
                    Total · all screens
                  </div>
                  {totalDemographicsLoading ? (
                    <div className="skeleton" style={{ height: 220, borderRadius: 10 }} />
                  ) : totalDemographics ? (
                    <DemographicsCard demographics={totalDemographics} />
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--fg-faint)', padding: '20px 0', textAlign: 'center' }}>No sensor data for this range</div>
                  )}
                </div>

                {sensorLocationsWithData.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 8 }}>
                      By location
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                      {sensorLocationsWithData.map(l => (
                        <div key={l.key}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.label}
                            {l.screenIds.length > 1 && (
                              <span style={{ fontWeight: 500, color: 'var(--fg-muted)' }}> · {l.screenIds.length} screens</span>
                            )}
                          </div>
                          {locationDemographicsLoading[l.key] ? (
                            <div className="skeleton" style={{ height: 220, borderRadius: 10 }} />
                          ) : (
                            <DemographicsCard demographics={locationDemographics[l.key]} />
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
        </div>
    </div>
  );
}

export function CreativesPage({ campaigns: initialCampaigns, clientUuid }: { campaigns: Campaign[]; clientUuid: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>(initialCampaigns);
  const [creatives, setCreatives] = useState<CreativeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState<CreativeSummary | null>(null);

  const campaignKey = campaigns.filter(c => c.broadsignId).map(c => c.id).join(',');

  const getUrlCreativeUrl = () => new URLSearchParams(window.location.search).get('creative_url');

  const openCreative = (c: CreativeSummary) => {
    setSelectedCreative(c);
    window.history.pushState(null, '', `/creatives?creative_url=${encodeURIComponent(c.creativeUrl ?? '')}`);
  };

  const closeCreative = () => {
    setSelectedCreative(null);
    window.history.pushState(null, '', '/creatives');
  };

  // Sync selectedCreative with URL on popstate (browser back/forward)
  useEffect(() => {
    const onPop = () => {
      const url = getUrlCreativeUrl();
      if (!url) { setSelectedCreative(null); return; }
      const match = creatives.find(c => c.creativeUrl === url);
      setSelectedCreative(match ?? null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [creatives]);

  useEffect(() => {
    if (initialCampaigns.length > 0) { setCampaigns(initialCampaigns); return; }
    if (!clientUuid) return;
    const fetch = clientUuid === '__reach__' ? getReachCampaigns() : getCampaigns(clientUuid);
    fetch.then(setCampaigns).catch(() => {});
  }, [clientUuid, initialCampaigns]);

  useEffect(() => {
    const valid = campaigns.filter(c => c.broadsignId);
    if (!valid.length) { setCreatives([]); setLoading(false); return; }
    const sd = valid.reduce((min, c) => c.startDate < min ? c.startDate : min, valid[0].startDate);
    setLoading(true);
    setCreatives([]);
    getCampaignCreativesSummary(campaignKey, sd)
      .then(results => {
        setCreatives(results);
        // Restore detail view if creative_url is in the URL (e.g. page refresh)
        const url = getUrlCreativeUrl();
        if (url) {
          const match = results.find(c => c.creativeUrl === url);
          if (match) setSelectedCreative(match);
        }
      })
      .catch(() => setCreatives([]))
      .finally(() => setLoading(false));
  }, [campaignKey]);

  if (selectedCreative) {
    return <CreativeDetailPage creative={selectedCreative} campaignUuids={campaignKey} onBack={closeCreative} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em' }}>Creatives</div>
        {!loading && creatives.length > 0 && (
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-soft)', padding: '2px 9px', borderRadius: 999 }}>{creatives.length}</span>
        )}
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>Loading creatives…</div>
      ) : creatives.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }}>◯</div>
          <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 4 }}>No creatives found</div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{campaigns.length === 0 ? 'No campaigns loaded.' : 'No creatives returned for these campaigns.'}</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 130px', alignItems: 'center', padding: '10px 20px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)' }}>
            {['Creative', 'Plays', 'Impressions'].map((h, i) => (
              <div key={i} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', textAlign: i === 2 ? 'right' : 'left' }}>{h}</div>
            ))}
          </div>
          {creatives.map((creative, idx) => {
            const isVideo = creative.type === 'video' || /\.(mp4|mov|webm)$/i.test(creative.creativeUrl ?? '');
            const filename = filenameFromUrl(creative.creativeUrl);
            const displayName = creative.name ?? filename ?? creative.creativeUrl ?? '';
            return (
              <div
                key={creative.creativeUrl ?? idx}
                onClick={() => openCreative(creative)}
                style={{ display: 'grid', gridTemplateColumns: '104px 1fr 110px 130px', alignItems: 'flex-start', padding: '12px 20px', borderBottom: idx < creatives.length - 1 ? '1px solid var(--border-subtle)' : 'none', transition: 'background 0.1s', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {creative.creativeUrl ? (
                  isVideo
                    ? <VideoThumb src={creative.creativeUrl} width={80} height={142} borderRadius={8} />
                    : <img src={creative.creativeUrl} alt="" style={{ width: 80, height: 142, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
                ) : isVideo ? (
                  <div style={{ width: 80, height: 142, borderRadius: 8, background: 'linear-gradient(160deg,#1e293b,#0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 24 }}>▶</div>
                ) : (
                  <div style={{ width: 80, height: 142, borderRadius: 8, background: 'var(--bg-surface-3)' }} />
                )}
                <div style={{ minWidth: 0, paddingRight: 16, paddingTop: 6 }}>
                  <div style={{ marginBottom: 4 }}>
                    <span style={{
                      fontSize: 10.5, fontWeight: 700,
                      color: isVideo ? 'var(--brand-blue)' : '#FF5C00',
                      background: isVideo ? 'rgba(59,130,246,0.10)' : 'rgba(245,158,11,0.12)',
                      border: `1px solid ${isVideo ? 'rgba(59,130,246,0.20)' : 'rgba(245,158,11,0.25)'}`,
                      padding: '2px 7px', borderRadius: 5,
                    }}>
                      {isVideo ? '▶ Video' : '◻ Image'}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--fg-strong)', marginBottom: 3, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                  {filename && displayName !== filename && (
                    <div style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</div>
                  )}
                </div>
                <div style={{ textAlign: 'left', paddingTop: 6 }} className="num">
                  <span style={{ fontWeight: 700, fontSize: 13, color: creative.plays ? 'var(--fg-strong)' : 'var(--fg-faint)' }}>
                    {creative.plays ? fmtFull(creative.plays) : '—'}
                  </span>
                </div>
                <div style={{ textAlign: 'right', paddingTop: 6 }} className="num">
                  <span style={{ fontWeight: 700, fontSize: 13, color: creative.impressions ? 'var(--fg-strong)' : 'var(--fg-faint)' }}>
                    {creative.impressions ? fmtFull(creative.impressions) : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
